import type { ReactNode } from 'react';
import { ArrowRight, CheckCircle2, ChevronDown, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthLink } from '@/lib/links';

export function FeatureHeroBadge({
  icon: Icon,
  children,
}: Readonly<{
  icon: LucideIcon;
  children: ReactNode;
}>) {
  return (
    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#7950A2]/12 border border-[#7950A2]/35 mb-6 font-mono text-xs">
      <Icon className="size-3.5 text-[#C29BF8]" />
      <span className="text-[#E3D1FC] font-semibold uppercase tracking-wider">{children}</span>
    </div>
  );
}

export function FeatureChecklist({ items }: Readonly<{ items: readonly string[] }>) {
  return (
    <ul className="space-y-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-slate-300">
          <CheckCircle2 className="size-5 text-emerald-400 mt-0.5 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function FeatureFaq({
  faqs,
}: Readonly<{ faqs: readonly { question: string; answer: string }[] }>) {
  return (
    <section className="mb-24 max-w-3xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {faqs.map((faq, i) => (
          <details
            key={i}
            className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden"
          >
            <summary className="flex items-center justify-between p-5 cursor-pointer text-white font-medium hover:bg-white/5 transition-colors">
              {faq.question}
              <ChevronDown className="size-4 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
            </summary>
            <div className="px-5 pb-5 text-slate-300 leading-relaxed">{faq.answer}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

export function FeatureStats({
  title,
  items,
}: Readonly<{
  title: string;
  items: readonly { value: string; label: string; desc: string }[];
}>) {
  return (
    <section className="mb-24">
      <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((stat, i) => (
          <div
            key={i}
            className="bg-slate-900/60 rounded-2xl p-6 border border-slate-800 text-center"
          >
            <div className="text-3xl font-bold text-white font-mono tracking-tight mb-2">
              {stat.value}
            </div>
            <div className="text-white font-semibold mb-1">{stat.label}</div>
            <div className="text-slate-400 text-sm">{stat.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FeatureSignup({
  title,
  buttonLabel,
  children,
}: Readonly<{
  title: string;
  buttonLabel: string;
  children: ReactNode;
}>) {
  return (
    <section className="text-center bg-slate-900/80 rounded-3xl p-8 sm:p-12 border border-slate-800">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight">{title}</h2>
      <p className="text-slate-300 mb-8 max-w-2xl mx-auto">{children}</p>
      <Button
        size="lg"
        className="bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold shadow-md shadow-[#7950A2]/25 px-10"
        onClick={() => window.location.assign(getAuthLink('/register'))}
      >
        {buttonLabel}
        <ArrowRight className="ml-2 size-5" />
      </Button>
    </section>
  );
}
